'use client';
import { alpha, Box, Button, Typography } from '@mui/material';

const MONO_FONT = 'var(--font-plex-mono), IBM Plex Mono, monospace';

interface SideSelectorProps {
  selectedSide: 'yes' | 'no';
  outcomeNameYes: string;
  outcomeNameNo: string;
  yesPrice: number;
  noPrice: number;
  yesBtnColor?: string;
  noBtnColor?: string;
  onYesClick: () => void;
  onNoClick: () => void;
  isFlipped?: boolean;
}

export function SideSelector({
  selectedSide,
  outcomeNameYes,
  outcomeNameNo,
  yesPrice,
  noPrice,
  yesBtnColor = '#4ec38a',
  noBtnColor = '#e0664f',
  onYesClick,
  onNoClick,
  isFlipped = false,
}: SideSelectorProps) {
  // Define data for the two slots (Top and Bottom) based on the flip state.
  // This ensures that the component logic (names, prices, colors, and clicks)
  // always aligns with the visual ordering.
  const topData = {
    name: isFlipped ? outcomeNameNo : outcomeNameYes,
    price: isFlipped ? noPrice : yesPrice,
    color: isFlipped ? noBtnColor : yesBtnColor,
    side: isFlipped ? ('no' as const) : ('yes' as const),
    onClick: isFlipped ? onNoClick : onYesClick,
  };

  const bottomData = {
    name: isFlipped ? outcomeNameYes : outcomeNameNo,
    price: isFlipped ? yesPrice : noPrice,
    color: isFlipped ? yesBtnColor : noBtnColor,
    side: isFlipped ? ('yes' as const) : ('no' as const),
    onClick: isFlipped ? onYesClick : onNoClick,
  };

  const renderSideButton = (data: typeof topData) => {
    const isActive = selectedSide === data.side;
    return (
      <Button
        fullWidth
        variant="contained"
        disableElevation
        onClick={data.onClick}
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1,
          bgcolor: isActive ? alpha(data.color, 0.16) : 'background.default',
          color: isActive ? data.color : 'text.secondary',
          fontWeight: 600,
          fontSize: '1rem',
          py: 1.375,
          px: 1.75,
          borderRadius: '10px',
          textTransform: 'none',
          border: '1px solid',
          borderColor: isActive ? alpha(data.color, 0.4) : 'divider',
          boxShadow: 'none',
          transition: 'all 0.12s ease',
          '&:hover': {
            bgcolor: isActive ? alpha(data.color, 0.22) : 'action.hover',
            boxShadow: 'none',
          },
        }}
      >
        <Typography
          sx={{
            fontFamily: MONO_FONT,
            fontSize: '0.75rem',
            fontWeight: 500,
            color: 'inherit',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {data.name}
        </Typography>
        <Typography
          sx={{
            fontFamily: MONO_FONT,
            fontSize: '1rem',
            fontWeight: 600,
            color: isActive ? data.color : 'text.primary',
            whiteSpace: 'nowrap',
          }}
        >
          {(data.price * 100).toFixed(0)}¢
        </Typography>
      </Button>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1.25, mb: 2.25 }}>
      {renderSideButton(topData)}
      {renderSideButton(bottomData)}
    </Box>
  );
}
